import { useCallback, useMemo } from 'react';
import { Button, Flex } from '@mantine/core';
import { Filter, Search } from 'react-feather';
import { Icon } from '@/components';
import classes from './FeatureBar.module.css';
import CustomListMapEntry from '@/views/list/custom-lists/CustomListMapEntry';
import { RedFlagWarningCard } from '@/views/map/RedFlagWarning/RedFlagWarningCard';
import { ReleaseSelector } from '@/shell/ReleaseSelector/ReleaseSelector';
import { ViewModeOptions } from '@/features/types';
import type { RedFlagWarning } from '@/features/wildfireSlice';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { openFilterForm } from '@/features/analysisMapSlice';
import { useSmallScreen } from '@/hooks/useWindowDimension';
import { useMobileInspectMode } from '@/hooks/useMobileInspectMode';
import { useNavigateWithSearch } from '@/hooks/useNavigateWithSearch';
import { useClientTerminology } from '@/views/list/NetworkTerms';
import { isInProjectUrl } from '@/helpers/url';

interface FeatureBarProps {
  shouldShowRedFlagWarnings?: boolean;
  setRedFlagWarnings: React.Dispatch<
    React.SetStateAction<RedFlagWarning[] | undefined>
  >;
}

export function FeatureBar({
  shouldShowRedFlagWarnings = false,
  setRedFlagWarnings,
}: FeatureBarProps) {
  const dispatch = useAppDispatch();
  const intl = useClientTerminology();
  const { currentViewMode, isListView } = useAppSelector((s) => s.analysisMap);
  const isFilterSelected = useAppSelector((s) => s.filterQuery.isSelected);
  const isMobileInspectMode = useMobileInspectMode();
  const isSmallScreen = useSmallScreen();
  const navigateWithSearch = useNavigateWithSearch();

  const shouldDisplay = useMemo(
    () =>
      !isMobileInspectMode &&
      (currentViewMode.mode === ViewModeOptions.LEGACY ||
        isInProjectUrl(currentViewMode)),
    [isMobileInspectMode, currentViewMode]
  );

  const variant = isSmallScreen && isListView ? 'list' : 'map';

  const handleFilterClick = useCallback(() => {
    dispatch(openFilterForm());
  }, [dispatch]);

  if (!shouldDisplay) return null;

  return (
    <Flex
      className={classes.featureBar}
      data-testid="feature-bar"
      data-variant={variant}
      gap="xs"
    >
      {isSmallScreen && (
        <>
          <Button
            data-testid="feature-bar-search"
            aria-label={intl.formatMessage({ id: 'search.placeholder' })}
            className={`${classes.button} ${classes.iconOnlyButton}`}
            variant="default"
            onClick={() => {
              navigateWithSearch('/search', { sessionOnly: true });
            }}
          >
            <Icon Component={Search} size={16} />
          </Button>
          <Button
            data-testid="map-filter-button-mobile"
            data-selected={isFilterSelected}
            onClick={handleFilterClick}
            className={classes.button}
            variant="default"
            leftSection={<Icon Component={Filter} size={16} />}
          >
            {intl.formatMessage({ id: 'analysis.filterButton' })}
          </Button>
        </>
      )}
      <ReleaseSelector />
      {shouldShowRedFlagWarnings && (
        <RedFlagWarningCard setRedFlagWarnings={setRedFlagWarnings} />
      )}
      <CustomListMapEntry />
    </Flex>
  );
}
